// // colDef/internshipColDef.tsx

import { InternshipType } from "@/services/internship.service";
import { formatDate } from "@/utils/formatDate.util";
import { createColumnHelper } from "@tanstack/react-table";
import { Eye, MoreVertical, Pencil, Trash2, Globe, ShieldClose } from "lucide-react";
import { ActionDropdown, ActionDropdownContent, ActionDropdownTrigger, DropdownNode } from "@/components/ActionDropdown";

const columnHelper = createColumnHelper<InternshipType>();

export type InternshipActionType = {
  id: string;
  name: string;
  action: "view" | "edit" | "delete" | "publish" | "close";
};

export const createInternshipColumns = (
  onActionClick: (payload: InternshipActionType) => void
) => [
    // 🔥 Title
    columnHelper.accessor("title", {
      header: "Title",
      cell: (info) => <span className="font-medium">{info.getValue()}</span>,
    }),

    // 🔥 Type
    columnHelper.accessor("type", {
      header: "Type",
      cell: (info) => (
        <span className="text-sm capitalize">{info.getValue()}</span>
      ),
    }),

    // 🔥 Domain
    columnHelper.accessor("domain", {
      header: "Domain",
      cell: (info) => (
        <span className="text-sm">{info.getValue() || "-"}</span>
      ),
    }),

    // 🔥 Location
    columnHelper.accessor("location", {
      header: "Location",
      cell: (info) => (
        <span className="text-sm">
          {info.row.original.is_remote ? "Remote" : (info.getValue() || "-")}
        </span>
      ),
    }),

    // 🔥 Duration
    columnHelper.accessor("duration_weeks", {
      header: "Duration",
      cell: (info) => <span className="text-sm">{info.getValue()} weeks</span>,
    }),

    // 🔥 Start Date
    columnHelper.accessor("start_date", {
      header: "Start Date",
      cell: (info) => (
        <span className="text-sm">
          {formatDate(info.getValue(), {
            locale: "en-CA",
            dateStyle: "short",
          })}
        </span>
      ),
    }),

    // 🔥 Status
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => {
        const val = (info.getValue() || "draft").toLowerCase();

        const config: Record<string, { label: string; className: string }> = {
          draft:  { label: "Draft",  className: "bg-yellow-100 text-yellow-800" },
          open:   { label: "Open",   className: "bg-gray-900 text-white" },
          closed: { label: "Closed", className: "bg-gray-100 text-gray-600" },
        };

        const { label, className } = config[val] ?? { label: val, className: "bg-gray-100 text-gray-600" };

        return (
          <span
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${className}`}
          >
            {label}
          </span>
        );
      },
    }),

    // 🔥 Actions (icons)
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => {
        const data = info.row.original;
        const status = (data.status || "draft").toLowerCase();

        const dropdownNodes: DropdownNode[] = [
          {
            type: "group",
            children: [
              {
                type: "item",
                label: "View Details",
                icon: <Eye size={14} />,
                onSelect: () => onActionClick({ id: data.id, name: data.title, action: "view" }),
              },
              {
                type: "item",
                label: "Edit Internship",
                icon: <Pencil size={14} />,
                onSelect: () => onActionClick({ id: data.id, name: data.title, action: "edit" }),
              },
              {
                type: "item",
                label: "Publish",
                icon: <Globe size={14} />,
                disabled: status !== "draft",   // enabled only for draft
                onSelect: () => onActionClick({ id: data.id, name: data.title, action: "publish" }),
              },
              {
                type: "item",
                label: "Close",
                icon: <ShieldClose size={14} />,
                disabled: status !== "open",    // enabled only for open
                onSelect: () => onActionClick({ id: data.id, name: data.title, action: "close" }),
              },
            ],
          },
          { type: "separator" },
          {
            type: "item",
            label: "Delete Internship",
            icon: <Trash2 size={14} />,
            className: "text-red-600 focus:text-red-600",
            iconClass: "text-red-600",
            onSelect: () => onActionClick({ id: data.id, name: data.title, action: "delete" }),
          },
        ];

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <ActionDropdown>
              <ActionDropdownTrigger
                triggerContent={<MoreVertical size={16} />}
                buttonVariant="ghost"
                buttonSize="icon"
                className={{ buttonClass: "h-8 w-8 p-0" }}
              />
              <ActionDropdownContent dropdownShape={dropdownNodes} align="end" />
            </ActionDropdown>
          </div>
        );
      },
    }),
  ];
